import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import {
  Upload,
  ShieldCheck,
  Award,
  KeyRound,
  Check,
  Loader2,
  BadgeCheck,
  FileBadge2,
  Fingerprint,
} from "lucide-react";
import { markKycCompleted } from "../lib/kyc";

const STEPS = [
  { label: "Identity", icon: Fingerprint },
  { label: "Classification", icon: Award },
  { label: "Authorization", icon: KeyRound },
];

export const KycWizard = ({ open, onOpenChange, onComplete }) => {
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [classification, setClassification] = useState(""); // "hnw" | "sophisticated"
  const [walletChecks, setWalletChecks] = useState({
    keys_verified: false,
    address_whitelisted: false,
    signature_verified: false,
  });
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(0);
      setFileName("");
      setUploaded(false);
      setUploading(false);
      setProgress(0);
      setClassification("");
      setWalletChecks({
        keys_verified: false,
        address_whitelisted: false,
        signature_verified: false,
      });
    }
  }, [open]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setUploading(true);
    setProgress(0);
    setUploaded(false);
    let p = 0;
    const t = setInterval(() => {
      p += 12;
      setProgress(Math.min(100, p));
      if (p >= 100) {
        clearInterval(t);
        setUploading(false);
        setUploaded(true);
      }
    }, 90);
  };

  const runWalletChecks = () => {
    setWalletChecks({ keys_verified: false, address_whitelisted: false, signature_verified: false });
    setTimeout(() => setWalletChecks((w) => ({ ...w, keys_verified: true })), 400);
    setTimeout(() => setWalletChecks((w) => ({ ...w, signature_verified: true })), 900);
    setTimeout(() => setWalletChecks((w) => ({ ...w, address_whitelisted: true })), 1500);
  };

  useEffect(() => {
    if (open && step === 2) runWalletChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, open]);

  const canNext = () => {
    if (step === 0) return uploaded;
    if (step === 1) return !!classification;
    if (step === 2) return Object.values(walletChecks).every(Boolean);
    return false;
  };

  const handleNext = () => {
    if (step < 2) {
      setStep((s) => s + 1);
    } else {
      markKycCompleted();
      toast.success("KYC & wallet whitelisting complete.");
      onOpenChange(false);
      onComplete?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-200 bg-white p-0" data-testid="kyc-modal">
        <div className="border-b border-slate-100 px-8 pt-8 pb-6">
          <DialogHeader>
            <div className="flex items-center gap-2 overline">
              <ShieldCheck className="h-3 w-3 brickstok-gold" /> Verification Wizard
            </div>
            <DialogTitle className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-900">
              Investor Onboarding
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              A one-time 3-step verification before your first allocation. Fully compliant with FCA COBS.
            </DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          <div className="mt-6 flex items-center gap-3" data-testid="kyc-stepper">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = i === step;
              const done = i < step;
              return (
                <React.Fragment key={s.label}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border transition-colors duration-200`}
                      style={{
                        background: done ? "#D4AF37" : active ? "#0F172A" : "#FFFFFF",
                        borderColor: done ? "#D4AF37" : active ? "#0F172A" : "#E2E8F0",
                        color: done || active ? "#FFFFFF" : "#94a3b8",
                      }}
                      data-testid={`kyc-step-indicator-${i}`}
                    >
                      {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <span
                      className={`text-xs font-semibold uppercase tracking-widest ${
                        active ? "text-slate-900" : done ? "text-slate-700" : "text-slate-400"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="h-px flex-1" style={{ background: done ? "#D4AF37" : "#E2E8F0" }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="min-h-[280px] px-8 py-6">
          {step === 0 && (
            <div data-testid="kyc-step-identity" className="space-y-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Identity Verification</h3>
              <p className="text-sm text-slate-500">
                Upload a clear photograph of your Passport or National ID card. Verification is automated via
                <span className="ml-1 rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">sumsub mock</span>.
              </p>

              <label
                htmlFor="kyc-file"
                className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 transition-colors hover:border-[#D4AF37] hover:bg-slate-50/50"
                data-testid="kyc-file-drop"
              >
                <Upload className="h-6 w-6 brickstok-gold" />
                <div className="mt-3 text-sm font-medium text-slate-800">
                  {fileName || "Click to upload Passport / National ID"}
                </div>
                <div className="mt-1 text-xs text-slate-500">JPG, PNG or PDF · max 10MB</div>
                <input
                  ref={fileRef}
                  id="kyc-file"
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  onChange={handleFile}
                  data-testid="kyc-file-input"
                />
              </label>

              {(uploading || uploaded) && (
                <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="kyc-verification-status">
                  <div className="flex items-center gap-2">
                    {uploaded ? (
                      <BadgeCheck className="h-4 w-4 brickstok-gold" />
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                    )}
                    <div className="text-sm font-medium text-slate-800">
                      {uploaded ? "Verification automated via sumsub mock — approved" : "Verification automated via sumsub mock…"}
                    </div>
                  </div>
                  <Progress value={progress} className="mt-3 h-1.5" />
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div data-testid="kyc-step-classification" className="space-y-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Investor Classification</h3>
              <p className="text-sm text-slate-500">
                Please self-certify your investor category. This is required under FCA COBS 4.12 rules.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  {
                    key: "hnw",
                    title: "High-Net-Worth",
                    sub: "Annual income ≥ £100k or net assets ≥ £250k",
                    icon: FileBadge2,
                  },
                  {
                    key: "sophisticated",
                    title: "Sophisticated Investor",
                    sub: "Certified experience in unlisted securities",
                    icon: Award,
                  },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = classification === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setClassification(opt.key)}
                      className="group text-left"
                      data-testid={`kyc-classification-${opt.key}`}
                    >
                      <div
                        className="rounded-md border p-5 transition-all duration-200"
                        style={{
                          borderColor: active ? "#D4AF37" : "#E2E8F0",
                          background: active ? "rgba(212,175,55,0.06)" : "#FFFFFF",
                          boxShadow: active ? "0 6px 20px -10px rgba(212,175,55,0.4)" : "none",
                        }}
                      >
                        <div
                          className="flex h-9 w-9 items-center justify-center rounded-md"
                          style={{
                            background: active ? "#D4AF37" : "#F1F5F9",
                            color: active ? "#0F172A" : "#334155",
                          }}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="mt-3 font-display text-lg font-semibold text-slate-900">{opt.title}</div>
                        <div className="mt-1 text-xs text-slate-500">{opt.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="kyc-step-wallet" className="space-y-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Wallet Authorization</h3>
              <p className="text-sm text-slate-500">
                Confirming your wallet address is being whitelisted on-chain. This authorises stablecoin distributions.
              </p>

              <div className="space-y-2 rounded-md border border-slate-200 bg-white p-5">
                {[
                  { key: "keys_verified", label: "Security keys verified" },
                  { key: "signature_verified", label: "Signature challenge accepted" },
                  { key: "address_whitelisted", label: "Wallet address whitelisted on-chain" },
                ].map((item) => {
                  const done = walletChecks[item.key];
                  return (
                    <div
                      key={item.key}
                      className="flex items-center justify-between rounded-md px-3 py-2 transition-colors duration-200"
                      style={{ background: done ? "rgba(212,175,55,0.08)" : "transparent" }}
                      data-testid={`kyc-wallet-check-${item.key}`}
                    >
                      <div className="text-sm text-slate-800">{item.label}</div>
                      {done ? (
                        <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest" style={{ color: "#8B6F1D" }}>
                          <Check className="h-3.5 w-3.5" /> ok
                        </div>
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      )}
                    </div>
                  );
                })}
              </div>

              {Object.values(walletChecks).every(Boolean) && (
                <div className="animate-in fade-in slide-in-from-bottom-1 flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 duration-300" data-testid="kyc-whitelist-success">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-emerald-800">Your wallet address is now whitelisted on-chain</div>
                    <div className="text-xs text-emerald-700 font-mono-num">0x71C…3a2f · Ethereum Mainnet</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row items-center gap-3 border-t border-slate-100 px-8 py-5 sm:justify-between">
          <div className="text-xs text-slate-500">
            Step <span className="font-mono-num text-slate-800">{step + 1}</span> of {STEPS.length}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep((s) => s - 1)}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="kyc-back-btn"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canNext()}
              className={step === 2 ? "text-slate-900 hover:brightness-95" : "bg-slate-900 text-white hover:bg-slate-800"}
              style={step === 2 ? { background: "#D4AF37" } : undefined}
              data-testid={step === 2 ? "kyc-finish-btn" : "kyc-next-btn"}
            >
              {step === 2 ? "Finish & Proceed" : "Continue"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KycWizard;
